import Phaser from 'phaser';

import { Subscription } from 'rxjs';
import { ResizeMetrics } from '../../model/resize-metrics';
import { TwentyOne, GameEvent } from 'game-logic';
import { CardView } from '../cards/card-view';
import { VerticalCardStackViewModel } from './vertical-card-stack-view-model';
import { TwentyOneMainScene } from '../../scenes/twentyone-main-scene';
import { CardViewModel } from '../cards/card-view-model';
import { Positioning } from '../../model/positioning';
import { UxMetrics } from '../../model/ux-metrics';
//import { DebugContainerVisual } from '../debug/debug-container-visual';

export class VerticalCardStackView extends Phaser.GameObjects.Container {
  private currentResizeMetrics?: ResizeMetrics;
  private subscriptionGroup: Subscription;

  private rectangle: Phaser.GameObjects.Rectangle;

  private cardViews: Map<number, CardView> = new Map();
  private destroyTweens: Phaser.Tweens.Tween[] = [];

  constructor(
    scene: TwentyOneMainScene,
    x: number,
    y: number,
    private viewModel: VerticalCardStackViewModel,
  ) {
    super(scene, x, y);

    //new DebugContainerVisual(scene, this);

    this.subscriptionGroup = new Subscription();

    this.rectangle = scene.add.rectangle(0, 0, 0, 0, 0xff00ff).setAlpha(0.0).setOrigin(0.5, 0.5);
    this.add(this.rectangle);

    this.setName(VerticalCardStackView.name);
    scene.add.existing(this);

    this.subscriptionGroup.add(
      this.viewModel.cardViewModelChanges$.subscribe((change) => {
        if (change.type === 'add') {
          this.addFromCurrent(scene, change.viewModel, change.duration, change.instant);
        } else if (change.type === 'removeToBottom') {
          this.removeToBottom(change.viewModel, change.duration, change.delay, change.instant);
        } else if (change.type === 'removeToWaste') {
          this.removeToWaste(change.viewModel, change.duration);
        }
      }),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    // Stop any tweens which are destroying child views
    this.destroyTweens.forEach((tween) => {
      tween.stop();
    });

    super.destroy(fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    this.currentResizeMetrics = resizeMetrics;

    this.cardViews.forEach((cardView) => cardView.onResize(resizeMetrics));
    this.refreshLayout(resizeMetrics);
  }

  private refreshLayout(resizeMetrics: ResizeMetrics): void {
    const columnStackRect = Positioning.localColumnStackRect(
      resizeMetrics.layoutConfig,
      resizeMetrics.scalingMetrics,
    );

    this.setSize(columnStackRect.width, columnStackRect.height);

    this.rectangle.setPosition(0, 0);
    this.rectangle.setSize(columnStackRect.width, columnStackRect.height);

    this.layoutCards();
  }

  private layoutCards() {
    Array.from(this.cardViews.values()).forEach((cardView, index) => {
      const endPt = Positioning.localCardStackPosition(
        index,
        this.viewModel.layoutConfig,
        this.currentResizeMetrics?.scalingMetrics,
      );
      cardView.setPosition(endPt.x, endPt.y);
    });
  }

  public async handleLogicEvents(event: GameEvent<TwentyOne.TwentyOneEvents>): Promise<void> {
    // Note: If you add any events here, please look at PlayerHandView implementation
    switch (event.type) {
      case 'onMoveCurrentToColumn':
        {
          const column = event.payload.cardColumn;
          if (column.pileId === this.viewModel.pileId) {
            await this.viewModel.addCard(
              event.payload.cardColumn,
              event.payload.cardIndex,
              event.instant,
            );
          }
        }
        break;
      case 'onColumnCleared':
        {
          const column = event.payload.cardColumn;
          if (column.pileId === this.viewModel.pileId) {
            await this.viewModel.removeAllCards(event.instant);
          }
        }
        break;
      default:
        break;
    }
  }

  private addFromCurrent(
    scene: TwentyOneMainScene,
    cardViewModel: CardViewModel,
    duration: number,
    instant: boolean,
  ): void {
    // Add the new card view
    const cardView = new CardView(scene, 0, 0, cardViewModel);
    if (this.currentResizeMetrics) {
      cardView.onResize(this.currentResizeMetrics);
    }
    this.add(cardView);

    const cardIndex = this.cardViews.size;
    this.cardViews.set(cardView.cardIndex, cardView);

    const layoutConfig = this.viewModel.layoutConfig;
    const startPt = Positioning.globalCenterPosition(
      layoutConfig,
      this.currentResizeMetrics?.scalingMetrics,
    );

    const localStartPt = new Phaser.Math.Vector2();
    this.getLocalPoint(startPt.x, startPt.y, localStartPt);

    const endPt = Positioning.localCardStackPosition(
      cardIndex,
      this.viewModel.layoutConfig,
      this.currentResizeMetrics?.scalingMetrics,
    );

    const scaleFactor = this.currentResizeMetrics?.scalingMetrics.scaleFactor ?? 1;
    const boardBottom = layoutConfig.boardHeight * scaleFactor;

    if (instant) {
      cardView.setPosition(0.0, endPt.y);
      cardView.flipCard(true, false, 1);
      return;
    }

    cardView.setPosition(0.0, boardBottom);
    cardView.setAlpha(0.0);
    cardView.setScale(0.85);
    cardView.flipCard(true, false, 0.85);

    this.scene.tweens.add({
      targets: cardView,
      y: endPt.y,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: duration,
      ease: Phaser.Math.Easing.Quartic.Out,
    });

    this.scene.tweens.add({
      targets: [cardView],
      scaleX: 1.05,
      scaleY: 1.05,
      duration: UxMetrics.CardBounceDuration,
      delay: duration * 0.75,
      ease: Phaser.Math.Easing.Sine.Out,
      yoyo: true,
    });
  }

  private removeToBottom(
    cardViewModel: CardViewModel,
    duration: number,
    delay: number,
    instant: boolean,
  ): void {
    const foundView = this.cardViews.get(cardViewModel.cardIndex);
    if (foundView) {
      if (instant) {
        foundView.destroy();
      } else {
        const layoutConfig = this.viewModel.layoutConfig;
        const scaleFactor = this.currentResizeMetrics?.scalingMetrics.scaleFactor ?? 1;
        const boardBottom = layoutConfig.boardHeight * scaleFactor;

        const destroyTween = this.scene.tweens.add({
          targets: [foundView],
          x: 0,
          y: boardBottom,
          rotation: Phaser.Math.DegToRad(Phaser.Math.Between(-45, 45)), // Random rotation
          duration: duration,
          delay: delay,
          scale: 0.8,
          ease: Phaser.Math.Easing.Cubic.In,
          onComplete: () => {
            foundView.destroy();
          },
        });
        this.destroyTweens.push(destroyTween);
      }
    }
    this.cardViews.delete(cardViewModel.cardIndex);
  }

  private removeToWaste(cardViewModel: CardViewModel, duration: number): void {
    const foundView = this.cardViews.get(cardViewModel.cardIndex);
    if (foundView) {
      const disposePt = Positioning.globalWastePosition(
        this.viewModel.layoutConfig,
        this.currentResizeMetrics?.scalingMetrics,
      );
      const localDisposePt = new Phaser.Math.Vector2();
      this.getLocalPoint(disposePt.x, disposePt.y, localDisposePt);

      const destroyTween = this.scene.tweens.add({
        targets: [foundView],
        x: localDisposePt.x,
        y: localDisposePt.y,
        duration: duration,
        ease: Phaser.Math.Easing.Quadratic.Out,
        onComplete: () => {
          foundView.destroy();
        },
      });
      this.destroyTweens.push(destroyTween);
    }
    this.cardViews.delete(cardViewModel.cardIndex);
  }
}
